/**
 * What the user is allowed to turn off, and what it costs them.
 *
 * Every stage of the funnel past retrieval is optional — that is a property of the
 * pipeline, not a concession: ORB verification, the set-symbol channel and OCR each
 * answer a different question, each has a download and a per-scan cost, and each can be
 * absent without the others breaking. Until now that was decided by what a bundle
 * happened to ship and by URL flags nobody remembers. This is the same set of choices,
 * named, explained and remembered.
 *
 * The schema is the source of truth and the UI is generated from it, the way the desktop
 * window's dialog is generated from the pydantic models rather than written out beside
 * them. {@link FIELDS} is a mapped type over {@link Settings}, so a new setting does not
 * compile until it has a label and a sentence saying what it does — writing that sentence
 * is part of adding a knob.
 *
 * Two scopes. Most settings apply to the next scan, because the stage simply reads them.
 * The ones marked `reload` choose how a model is built, which happens once at boot; the
 * panel offers a reload rather than pretending otherwise. Turning a stage back *on* may
 * need its asset, which is fetched on demand — see `ensureStage` in `assets.ts`.
 */

export interface Settings {
  /** Geometric verification of the shortlist against the index's ORB references. */
  orb: boolean;
  /** The printed collector number, read to separate two printings of one artwork. */
  ocr: boolean;
  /** The printed set symbol, matched to separate one artwork's reprints across sets. */
  setSymbols: boolean;
  /** Read every card upside down as well and keep whichever retrieves better. */
  flipCheck: boolean;
  /** Retry a rejected read with a suspected sleeve ring trimmed off. */
  sleeveRetry: boolean;
  /**
   * Settle an ambiguous read whose tied top printings lie within this many cents of each
   * other by taking the more valuable one. "0" is off. Strings, like every option field.
   */
  priceTie: "0" | "50" | "150" | "300" | "500" | "1000";
  /** Detector input raster. */
  detector: "320" | "416" | "640";
  /** Where the models run. */
  backend: "auto" | "webgl" | "webgpu" | "wasm";
  /** Sound on a find, pitched by the printing's price. */
  chime: boolean;
  /** A console line per frame, not just per recognition. */
  trace: boolean;
}

export type SettingKey = keyof Settings;

export interface Field<K extends SettingKey> {
  /** Section heading in the panel; fields are shown in declaration order within it. */
  group: "Identification" | "Models" | "Session";
  label: string;
  /** One sentence: what it does, and what turning it off costs. */
  help: string;
  /** Absent for a toggle; the choices, in order, for anything else. */
  options?: ReadonlyArray<{ value: Settings[K]; label: string }>;
  /** Whether the change only takes effect on the next load. */
  reload?: true;
}

/**
 * The stages a fresh install runs.
 *
 * Everything past retrieval starts off. That is affordable because `decide` no longer
 * treats a missing verification as a failed one: a leader that clears its retrieval score
 * and leads the next artwork by the measured gap is accepted on retrieval alone, which on
 * 395 hardened frames was 94.2% of the reads verification would have accepted. What is off
 * is not downloaded either — no OpenCV-WASM, no descriptor index, no OCR model, no symbol
 * table — so the boot set drops by about 36 MB and the per-card cost by the shortlist's
 * range reads. Anyone who wants the close calls resolved turns them back on in the panel,
 * and they load on the spot.
 */
export const DEFAULTS: Settings = {
  orb: false,
  ocr: false,
  setSymbols: false,
  flipCheck: true,
  sleeveRetry: false,
  priceTie: "150",
  detector: "320",
  backend: "auto",
  chime: true,
  trace: false,
};

export const FIELDS: { [K in SettingKey]: Field<K> } = {
  orb: {
    group: "Identification",
    label: "Geometric verification",
    help:
      "Matches the card's own features against the index's references — the evidence that " +
      "separates a look-alike from the card in front of the camera. Off, a card that leads " +
      "its shortlist clearly is still accepted on retrieval alone; what needs this is the " +
      "close call, where two artworks retrieve almost equally well. Costs a 10 MB library " +
      "and a ~15 KB read per shortlisted artwork.",
  },
  ocr: {
    group: "Identification",
    label: "Read the collector number",
    help:
      "The only channel that tells two printings of one artwork apart inside a single set. " +
      "Runs only when the cheaper evidence has not already accepted. Costs a 21 MB model, " +
      "fetched after the camera starts.",
  },
  setSymbols: {
    group: "Identification",
    label: "Match the set symbol",
    help:
      "Separates reprints across sets, and is the only thing that can on a frame that " +
      "prints no collector number at all. Runs only on ambiguous reads. Costs 4.6 MB.",
  },
  flipCheck: {
    group: "Identification",
    label: "Read upside down too",
    help:
      "Reads a card at both orientations and keeps the better match, for cards that arrive " +
      "the wrong way up. Skipped automatically when the upright reading is already clear, " +
      "so turning it off only matters for the doubtful ones.",
  },
  sleeveRetry: {
    group: "Identification",
    label: "Retry through sleeves",
    help:
      "When a read is rejected and the crop looks like it caught a sleeve's edge, trims the " +
      "ring and reads once more. Costs a second identification on rejected frames only.",
  },
  priceTie: {
    group: "Identification",
    label: "Settle same-score printings by price",
    help:
      "When an uncertain read's top printings tie on their match percentage and their " +
      "values lie within this amount of each other, the more valuable printing is taken " +
      "and the row is marked “by price” instead of waiting for review — the money is the " +
      "same either way. A tie that includes a different card, an unpriced printing or " +
      "mixed currencies is never settled. Off keeps every such card in the review queue.",
    options: [
      { value: "0", label: "Off" },
      { value: "50", label: "$0.50" },
      { value: "150", label: "$1.50" },
      { value: "300", label: "$3" },
      { value: "500", label: "$5" },
      { value: "1000", label: "$10" },
    ],
  },
  detector: {
    group: "Models",
    label: "Detector input",
    help:
      "The raster the card detector sees. 320 is the measured mobile sweet spot: it " +
      "kept full detection coverage in the camera clips while cutting WebGL work by a " +
      "third versus 416. 640 is the original export.",
    options: [
      { value: "320", label: "320 px (fast)" },
      { value: "416", label: "416 px" },
      { value: "640", label: "640 px" },
    ],
    reload: true,
  },
  backend: {
    group: "Models",
    label: "Run models on",
    help:
      "Automatic uses the CPU on iPhone and iPad to keep scanning within the browser's memory budget; " +
      "other devices prefer WebGPU. Failed GPU startup restarts once with the CPU runtime. " +
      "The explicit choices are useful for diagnosis.",
    options: [
      { value: "auto", label: "Auto" },
      { value: "webgl", label: "WebGL2" },
      { value: "webgpu", label: "WebGPU" },
      { value: "wasm", label: "CPU" },
    ],
    reload: true,
  },
  chime: {
    group: "Session",
    label: "Chime on a find",
    help: "A tone per accepted card, pitched by the better of its two printed finishes.",
  },
  trace: {
    group: "Session",
    label: "Per-frame logging",
    help:
      "Adds a console line for every frame instead of one block per recognition — a " +
      "firehose at 30 fps, and the only way to see why the gate never settles.",
  },
};

const STORE_KEY = "webscan2:settings";

/**
 * The URL flags this file replaced, still honoured for one load.
 *
 * They predate the panel, they are in the source comments and in people's history, and a
 * flag that silently stopped working would be worse than one more line here. A flag wins
 * over the stored value and is not written back — it is a thing you try, not a thing you
 * chose.
 */
const URL_FLAGS: ReadonlyArray<[RegExp, Partial<Settings>]> = [
  [/[?&]wasm\b/, { backend: "wasm" }],
  [/[?&]webgl\b/, { backend: "webgl" }],
  [/[?&]webgpu\b/, { backend: "webgpu" }],
  [/[?&]det320\b/, { detector: "320" }],
  [/[?&]det640\b/, { detector: "640" }],
  [/[?&]trace\b/, { trace: true }],
  [/[?&]noorb\b/, { orb: false }],
  [/[?&]noocr\b/, { ocr: false }],
];

function stored(): Partial<Settings> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Key by key against the defaults: a stored file from an older build carries keys
    // that no longer exist and misses ones that now do, and a whole-object assign would
    // put both kinds of nonsense into the running config.
    for (const key of Object.keys(DEFAULTS) as SettingKey[]) {
      const value = parsed[key];
      const field = FIELDS[key];
      if (field.options) {
        if (field.options.some((option) => option.value === value)) out[key] = value;
      } else if (typeof value === "boolean") {
        out[key] = value;
      }
    }
    return out as Partial<Settings>;
  } catch {
    return {};
  }
}

function fromUrl(): Partial<Settings> {
  const search = typeof location === "undefined" ? "" : location.search;
  return URL_FLAGS.reduce<Partial<Settings>>(
    (out, [pattern, values]) => (pattern.test(search) ? { ...out, ...values } : out),
    {},
  );
}

/** The live configuration. Read it at the point of use — it changes under you. */
export const settings: Settings = { ...DEFAULTS, ...stored(), ...fromUrl() };

/** Keys whose stored value a URL flag is currently overriding, so the UI can say so. */
export const overridden = new Set(Object.keys(fromUrl()) as SettingKey[]);

/** The price-tie threshold in integer cents; 0 when the policy is off. Read at use. */
export function priceTieCents(): number {
  return Number(settings.priceTie) || 0;
}

type Listener = (key: SettingKey) => void;
const listeners = new Set<Listener>();

export function onSettingChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

export function setSetting<K extends SettingKey>(key: K, value: Settings[K]): void {
  if (settings[key] === value) return;
  settings[key] = value;
  try {
    // Only what differs from the defaults, so a later change of default reaches anyone
    // who never expressed an opinion about that knob.
    const diff: Partial<Settings> = {};
    for (const name of Object.keys(DEFAULTS) as SettingKey[]) {
      if (settings[name] !== DEFAULTS[name]) diff[name] = settings[name] as never;
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(diff));
  } catch {
    // Private mode, or a full quota. The setting still applies to this session.
  }
  for (const listener of listeners) listener(key);
}

export function resetSettings(): void {
  for (const key of Object.keys(DEFAULTS) as SettingKey[]) {
    setSetting(key, DEFAULTS[key] as never);
  }
}
